/**
 * EmailChangeForm Component
 *
 * Form for changing the user's email address with password verification.
 * Uses 2-column grid: "Nowy email" (left) + "Potwierdź hasłem" (right).
 *
 * @module pages/SettingsPage/components/EmailChangeForm
 */

import {
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  memo,
  useCallback,
  useState,
} from 'react';
import { Button, Group, PasswordInput, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/types/api';
import { profileService } from '@/services/api';
import { NotificationService } from '@/services/notificationService';

import styles from './EmailChangeForm.module.css';

interface EmailChangeFormProps {
  updateUserEmail: (email: string) => void;
}

export const EmailChangeForm = memo(function EmailChangeForm({
  updateUserEmail,
}: EmailChangeFormProps): ReactElement {
  const { t } = useTranslation();

  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const handleNewEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setNewEmail(event.currentTarget.value);
    },
    []
  );

  const handleEmailPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmailPassword(event.currentTarget.value);
    },
    []
  );

  const handleUpdateEmail = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmedEmail = newEmail.trim();
      if (!trimmedEmail || !emailPassword) return;

      setIsUpdatingEmail(true);
      try {
        const response = await profileService.updateEmail({
          newEmail: trimmedEmail,
          currentPassword: emailPassword,
        });
        updateUserEmail(response.data.email);
        setNewEmail('');
        setEmailPassword('');
        NotificationService.showSuccess(
          t('settings.account.email.success'),
          true
        );
      } catch (error: unknown) {
        const errorCode = (error as ApiError).backendErrorCode;
        if (errorCode === 'ACCOUNT_EMAIL_ALREADY_IN_USE') {
          NotificationService.showError(
            t('settings.account.email.alreadyInUse')
          );
          return;
        }
        if (errorCode === 'ACCOUNT_INVALID_CURRENT_PASSWORD') {
          NotificationService.showError(
            t('settings.account.email.invalidPassword')
          );
          return;
        }
        console.error('Failed to update email:', error);
        NotificationService.showError(t('common.error'));
      } finally {
        setIsUpdatingEmail(false);
      }
    },
    [newEmail, emailPassword, updateUserEmail, t]
  );

  const isEmailFormValid =
    newEmail.trim().length > 0 && emailPassword.length > 0;

  return (
    <form onSubmit={handleUpdateEmail}>
      <div className={styles.grid}>
        <TextInput
          label={t('settings.account.email.newEmail')}
          placeholder={t('settings.account.email.newEmailPlaceholder')}
          type="email"
          value={newEmail}
          onChange={handleNewEmailChange}
          autoComplete="email"
        />

        <PasswordInput
          label={t('settings.account.email.currentPassword')}
          placeholder={t('settings.account.email.currentPasswordPlaceholder')}
          value={emailPassword}
          onChange={handleEmailPasswordChange}
          autoComplete="current-password"
        />
      </div>

      <Group justify="flex-end" mt="lg">
        <Button
          type="submit"
          loading={isUpdatingEmail}
          disabled={!isEmailFormValid}
        >
          {t('settings.account.email.save')}
        </Button>
      </Group>
    </form>
  );
});
